/**
 * Principles Hook
 * Reusable hooks for leadership principles data management
 * 
 * Implements resilient data fetching:
 * 1. Try to fetch from API
 * 2. On success: return data and cache to localStorage
 * 3. On failure: check localStorage cache, then fallback to JSON file
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/api/api.client';
import { API_ENDPOINTS } from '@/api/endpoints';
import type {
  Principle,
  ApiResponse,
  CreatePrincipleRequest,
  UpdatePrincipleRequest
} from '@/api/types';
import fallbackData from '@/data/fallback-principles.json';

// Query keys
export const PRINCIPLES_KEY = ['principles'];
export const PRINCIPLE_KEY = (id: number) => ['principles', id];

// LocalStorage key for caching (v2 adds bibleRefEn)
const PRINCIPLES_CACHE_KEY = 'yim_principles_cache_v2';
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

// Cleanup old cache versions on load
if (typeof window !== 'undefined') {
  localStorage.removeItem('yim_principles_cache'); // Remove v1 cache
}

interface CachedPrinciples {
  data: Principle[];
  timestamp: number;
}

/**
 * Get cached principles from localStorage
 */
function getCachedPrinciples(): Principle[] | null {
  try {
    const cached = localStorage.getItem(PRINCIPLES_CACHE_KEY);
    if (!cached) return null;

    const parsed: CachedPrinciples = JSON.parse(cached);

    // Check if cache is still valid (within 24 hours)
    if (Date.now() - parsed.timestamp < CACHE_EXPIRY_MS) {
      return parsed.data;
    }

    // Cache expired, remove it
    localStorage.removeItem(PRINCIPLES_CACHE_KEY);
    return null;
  } catch {
    return null;
  }
}

/**
 * Save principles to localStorage cache
 */
function cachePrinciples(data: Principle[]): void {
  try {
    const cacheData: CachedPrinciples = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(PRINCIPLES_CACHE_KEY, JSON.stringify(cacheData));
  } catch {
    // Silently fail if localStorage is full or unavailable
  }
}

/**
 * Get fallback principles from JSON file
 */
function getFallbackPrinciples(): Principle[] {
  return fallbackData.principles.map((p) => ({
    id: p.id,
    order: p.order,
    titleFr: p.titleFr,
    titleEn: p.titleEn,
    descriptionFr: p.descriptionFr,
    descriptionEn: p.descriptionEn,
    bibleRef: p.bibleRef,
    bibleRefEn: p.bibleRefEn,
    icon: p.icon,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }));
}

/**
 * Fetch all principles with resilient fallback strategy
 * 
 * Strategy:
 * 1. Try to fetch from API
 * 2. On success: cache to localStorage and return data
 * 3. On failure: check localStorage cache
 * 4. If no cache: return fallback JSON data
 */
export function usePrinciples() {
  return useQuery<Principle[], Error>({
    queryKey: PRINCIPLES_KEY,
    queryFn: async () => {
      try {
        const response = await apiClient.get<ApiResponse<Principle[]>>(
          API_ENDPOINTS.PRINCIPLES.BASE
        );
        const principles = response.data.data;

        // Cache successful response
        cachePrinciples(principles);

        return principles;
      } catch {
        // Try localStorage cache first
        const cached = getCachedPrinciples();
        if (cached && cached.length > 0) {
          return cached;
        }

        // Fallback to JSON file
        return getFallbackPrinciples();
      }
    },
    staleTime: 60000, // Principles don't change often
    retry: 1, // Only retry once before falling back
    retryDelay: 1000,
  });
}

/**
 * Hook to get LIFE values from fallback data
 * These are static values that don't come from the API
 */
export function useLifeValues() {
  return fallbackData.lifeValues;
}

/**
 * Fetch a single principle by ID
 */
export function usePrinciple(id: number) {
  return useQuery<ApiResponse<Principle>, Error, Principle>({
    queryKey: PRINCIPLE_KEY(id),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Principle>>(
        API_ENDPOINTS.PRINCIPLES.BY_ID(id)
      );
      return response.data;
    },
    select: (data) => data.data,
    enabled: !!id,
  });
}

/**
 * Create a new principle
 */
export function useCreatePrinciple() {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<Principle>, Error, CreatePrincipleRequest>({
    mutationFn: async (data: CreatePrincipleRequest) => {
      const response = await apiClient.post<ApiResponse<Principle>>(
        API_ENDPOINTS.PRINCIPLES.BASE,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      // Clear cache when principles are modified
      localStorage.removeItem(PRINCIPLES_CACHE_KEY);
      queryClient.invalidateQueries({ queryKey: PRINCIPLES_KEY });
    },
  });
}

/**
 * Update a principle
 */
export function useUpdatePrinciple() {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<Principle>, Error, { id: number; data: UpdatePrincipleRequest }>({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.patch<ApiResponse<Principle>>(
        API_ENDPOINTS.PRINCIPLES.BY_ID(id),
        data
      );
      return response.data;
    },
    onSuccess: (_, { id }) => {
      // Clear cache when principles are modified
      localStorage.removeItem(PRINCIPLES_CACHE_KEY);
      queryClient.invalidateQueries({ queryKey: PRINCIPLES_KEY });
      queryClient.invalidateQueries({ queryKey: PRINCIPLE_KEY(id) });
    },
  });
}

/**
 * Delete a principle
 */
export function useDeletePrinciple() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (id: number) => {
      await apiClient.delete(API_ENDPOINTS.PRINCIPLES.BY_ID(id));
    },
    onSuccess: () => {
      localStorage.removeItem(PRINCIPLES_CACHE_KEY);
      queryClient.invalidateQueries({ queryKey: PRINCIPLES_KEY });
      queryClient.invalidateQueries({ queryKey: ['trash'] });
    },
  });
}

/**
 * Utility to clear principles cache
 * Useful for forcing a fresh fetch
 */
export function clearPrinciplesCache(): void {
  localStorage.removeItem(PRINCIPLES_CACHE_KEY);
}

export default usePrinciples;
